<template>
  <div class="input-date-wrapper">
    <!-- Label with validation indicators -->
    <label
      v-if="label && label !== ''"
      :for="elemId"
      class="input-date-label"
      :class="{
        'text-warning': !hasValidation && isRequired,
        'text-success': hasValidation && isRequired
      }"
    >
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-indicator">*</span>
      {{ label }}
    </label>

    <!-- PrimeVue DatePicker (date only, no time) -->
    <DatePicker
      :id="elemId"
      v-model="dateValue"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :invalid="!hasValidation && isRequired"
      showIcon
      iconDisplay="input"
      :dateFormat="dateFormat"
      fluid
      @update:modelValue="onDateChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import DatePicker from 'primevue/datepicker'
import { useDateFormatter } from '../composables/useDateFormatter'

const props = defineProps({
  elemId: String,
  placeholder: String,
  label: String,
  validation: {
    type: Function as any,
    default: () => true
  },
  ngRequired: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  modelValue: {
    type: String,
    default: ''
  },
  value: {
    type: String,
    default: ''
  }
})

const emit = defineEmits<{
  'update:modelValue': [value: any];
  'input': [value: string];
}>()

// Use date formatter composable
const { parseBackendDate, fromDateToApiDate, dateFormat } = useDateFormatter()

// Reactive data - PrimeVue DatePicker expects Date object
const dateValue = ref<Date | null>(parseBackendDate(props.modelValue || props.value))

// Computed properties
const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
)

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
)

const hasValidation = computed((): boolean => {
  try {
    return (props.validation as () => boolean)()
  } catch (error) {
    return true // Default to valid to prevent crashes
  }
})

// Methods
function onDateChange(newDate: any): void {
  // Don't emit if date is invalid or null
  if (!newDate || isNaN(new Date(newDate).getTime())) {
    return
  }

  const formattedDate = fromDateToApiDate(newDate)

  // Emit the formatted string (backward compatible with original format)
  emit('update:modelValue', formattedDate)
  emit('input', formattedDate) // Keep for backward compatibility
}

// Watchers
watch(() => props.modelValue, (newVal: string) => {
  const newDate = parseBackendDate(newVal)
  const currentFormatted = fromDateToApiDate(dateValue.value)

  if (currentFormatted !== newVal) {
    dateValue.value = newDate
  }
})

watch(() => props.value, (newVal: string) => {
  const newDate = parseBackendDate(newVal)
  const currentFormatted = fromDateToApiDate(dateValue.value)

  if (currentFormatted !== newVal) {
    dateValue.value = newDate
  }
})
</script>

<style scoped>
.input-date-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.input-date-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--text-color);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.input-date-label.text-warning {
  color: var(--yellow-500);
}

.input-date-label.text-success {
  color: var(--green-500);
}

.required-indicator {
  color: var(--red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--yellow-500);
}

.text-success {
  color: var(--green-500);
}
</style>
